/** @format */

/* eslint @typescript-eslint/prefer-nullish-coalescing: "warn" -- Legacy code: do not use as an example. */
import isPropValid from '@emotion/is-prop-valid';
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import { ValidationError } from '@mpb-com/feather/components/atoms/validation-error';
import { useTranslation } from 'next-i18next';
import React, {
    FunctionComponent,
    ReactNode,
    useEffect,
    useRef,
    useState,
} from 'react';
import styled from 'styled-components';

import AccordionDownSVG from '@toucan/client/global/icons/accordion-down.svg';
import SmallArrowIcon from '@toucan/client/global/icons/legacy/small-arrow.svg';

import { styles as formContainerStyles } from './formContainer.styles';
import { styles as inlineContainerStyles } from './inlineContainer.styles';
import { styles } from './select.styles';

// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
export enum SelectVariant {
    BLOCK = 'block',
    INLINE = 'inline',
}

// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
export enum SelectSize {
    MEDIUM = 'medium',
    SMALL = 'small',
}

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type SelectOption = {
    value: string;
    name: string;
};

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type SelectProps = {
    /**
     * The form field label
     */
    label?: string;

    /**
     * An accessible name for the select when no visible `label` is rendered.
     * Required for screen reader users when `label` is omitted — the visible
     * label is the only other source of an accessible name.
     */
    'aria-label'?: string;

    /**
     * The form field name
     */
    name: string;

    /**
     * The html element ID, must be unique
     */
    id?: string;

    /**
     * The input type
     * @default text
     */
    // eslint-disable-next-line @mpb-com/mpb/prefer-enum-over-string-union -- Legacy code: do not use as an example.
    type?: 'text' | 'number' | 'password' | 'email' | 'tel';

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Whether the button is disabled
     *
     * @default false
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    disabled?: boolean;

    /**
     * The value of the autocomplete attribute
     */
    autoComplete?: string;

    /**
     * Whether this field is required
     *
     * @default false
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    required?: boolean;

    /**
     * The select value
     */
    value?: string | string[] | number;

    /**
     * The error message to show when the input is invalid.
     */
    error?: string;

    /**
     * Whether the input has been touched. Used by validation to update error messages
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    touched?: boolean;

    /**
     * Custom className
     */
    className?: string;

    /**
     * The component style, one of:
     * 1. BLOCK
     * 2. INLINE
     *
     * @default BLOCK
     */
    variant?: SelectVariant;

    /**
     * The size of the component, one of:
     * 1. MEDIUM
     * 2. SMALL
     *
     * @default MEDIUM
     */
    displaySize?: SelectSize;

    /**
     *  Whether or not to display the down arrow in the select.
     *
     * @default true
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    displayArrow?: boolean;

    /**
     * Whether or not to fill parent html element.
     * Allows for larger clickable area
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    fillParent?: boolean;

    /**
     * The select options
     */
    children?: ReactNode;

    /**
     * Element change event handler.
     *
     * @param event {ChangeEvent<HTMLSelectElement>} The change event
     */
    onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;

    /**
     * Element blur event handler.
     *
     * @param event {FocusEvent<HTMLSelectElement>} The blur event
     */
    onBlur?: (event: React.FocusEvent<HTMLSelectElement>) => void;

    /**
     * Element focus event handler.
     *
     * @param event {FocusEvent<HTMLSelectElement>} The focus event
     */
    onFocus?: (event: React.FocusEvent<HTMLSelectElement>) => void;
    placeholder?: string;
};

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type StyledContainerProps = Pick<
    SelectProps,
    'fillParent' | 'displayArrow' | 'children' | 'variant'
>;

// eslint-disable-next-line @mpb-com/mpb/styled-component-prefix -- Legacy code: do not use as an example.
const FormContainer = styled('div')<StyledContainerProps>`
    ${formContainerStyles}
`;

// eslint-disable-next-line @mpb-com/mpb/single-styled-component, @mpb-com/mpb/styled-component-prefix -- Legacy code: do not use as an example.
const InlineContainer = styled('div')<StyledContainerProps>`
    ${inlineContainerStyles}
`;

const StyledContainer = ({
    variant,
    fillParent,
    displayArrow,
    children,
}: Partial<SelectProps>) => {
    return React.createElement(
        variant === SelectVariant.BLOCK ? FormContainer : InlineContainer,
        { fillParent, displayArrow },
        children
    );
};

// eslint-disable-next-line @mpb-com/mpb/single-styled-component -- Legacy code: do not use as an example.
const StyledSelect = styled('select').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export {
    Select as CompatibleSelect,
    SelectSkeleton as CompatibleSelectSkeleton,
} from './components/compatible';

export const Select: FunctionComponent<SelectProps> = ({
    label,
    id,
    name,
    value,
    error,
    touched,
    disabled,
    children,
    autoComplete,
    variant = SelectVariant.BLOCK,
    displaySize = SelectSize.MEDIUM,
    displayArrow = true,
    fillParent = false,
    required = false,
    className,
    placeholder,
    ...otherProps
}: SelectProps) => {
    const [selectText, setSelectText] = useState('');
    const ref = useRef<HTMLSelectElement>(null);
    const { t } = useTranslation();

    useEffect(() => {
        const hasOptions = ref?.current?.options;

        if (hasOptions) {
            const options = Array.from(ref?.current?.options);
            const option = options.find((option) => option.value === value);

            setSelectText(option?.textContent ?? '');
        }
    }, [value]);

    /**
     * Update the select text when the selected index changes
     */
    useEffect(() => {
        setSelectText(
            ref?.current?.options?.[ref?.current?.selectedIndex]?.text ?? ''
        );
    }, [ref.current?.selectedIndex]);

    return (
        <ValidationError
            disabled={disabled}
            error={error}
            touched={touched}
            className={className}
        >
            <StyledContainer
                variant={variant}
                fillParent={fillParent}
                displayArrow={displayArrow}
            >
                <StyledSelect
                    label={label}
                    id={id || name}
                    name={name}
                    error={error}
                    touched={touched}
                    data-value={value}
                    disabled={disabled}
                    value={value}
                    ref={ref}
                    variant={variant}
                    displaySize={displaySize}
                    displayArrow={displayArrow}
                    fillParent={fillParent}
                    aria-required={required}
                    autoComplete={autoComplete}
                    {...otherProps}
                >
                    {children}
                </StyledSelect>
                {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                {label && (
                    <label htmlFor={id || name} className="label">
                        {label}
                        {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                        {required && <sup>*</sup>}
                    </label>
                )}
                <span className="text" aria-hidden="true">
                    {selectText || t('Select')}
                </span>
                {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                {displayArrow &&
                    (variant == SelectVariant.INLINE ? (
                        <SmallArrowIcon />
                    ) : (
                        <Icon
                            className="arrow"
                            variant={IconVariant.DECORATIVE}
                            size={IconSize.S}
                        >
                            <AccordionDownSVG />
                        </Icon>
                    ))}
            </StyledContainer>
        </ValidationError>
    );
};
